import { FC, useState } from "react";
import { StarRating } from "@components/StarRating/StarRating";
import styles from "./ReviewTabHead.module.css";
import ReviewForm from "@components/Forms/ReviewForm/ReviewForm";
import { Modal } from "@components/Modal/Modal";
import { ReviewStats } from "@types";

interface ReviewTabHeadProps {
  reviewsData: ReviewStats;
  productId?: string;
  productName?: string;
  onReviewSubmit?: () => void;
}

export const ReviewTabHead: FC<ReviewTabHeadProps> = ({
  reviewsData,
  productId,
  productName,
  onReviewSubmit,
}) => {
  const [showReviewForm, setShowReviewForm] = useState(false);

  // Calculate percentage for each rating
  const getPercentage = (count: number) => {
    return reviewsData.totalReviews > 0
      ? (count / reviewsData.totalReviews) * 100
      : 0;
  };

  // Функция для правильного склонения слова "отзывы"
  const getReviewCountText = (count: number) => {
    const lastDigit = count % 10;
    const lastTwoDigits = count % 100;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
      return `${count} отзывов`;
    }

    if (lastDigit === 1) {
      return `${count} отзыв`;
    }

    if (lastDigit >= 2 && lastDigit <= 4) {
      return `${count} отзыва`;
    }

    return `${count} отзывов`;
  };

  // Обработчик успешной отправки формы
  const handleFormSuccess = () => {
    setShowReviewForm(false);
    if (onReviewSubmit) {
      onReviewSubmit();
    }
  };

  // Обработчик закрытия модального окна
  const handleModalClose = () => {
    setShowReviewForm(false);
  };

  return (
    <>
      <div className={styles.widgetHead}>
        {/* Block 1: Average Rating */}
        <div className={styles.averageBlock}>
          <div className={styles.ratingFigure}>
            {reviewsData.averageRating.toFixed(1)}
          </div>
          <StarRating rating={reviewsData.averageRating} variant="large" />
          <div className={styles.totalReviews}>
            {getReviewCountText(reviewsData.totalReviews)}
          </div>
        </div>

        {/* Block 2: Rating Breakdown */}
        <div className={styles.breakdownBlock}>
          {[5, 4, 3, 2, 1].map((rating) => {
            const ratingKey = rating as 1 | 2 | 3 | 4 | 5;
            return (
              <div key={rating} className={styles.breakdownRow}>
                <StarRating rating={rating} variant="small" />
                <span className={styles.reviewCount}>
                  ({reviewsData.ratingDistribution[ratingKey]})
                </span>
                <div className={styles.scaleContainer}>
                  <div
                    className={styles.scaleBar}
                    style={{
                      width: `${getPercentage(
                        reviewsData.ratingDistribution[ratingKey]
                      )}%`,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Block 3: Button */}
        <button
          className={styles.addReviewButton}
          onClick={() => setShowReviewForm(true)}
        >
          Добавить отзыв
        </button>
      </div>

      <Modal isOpen={showReviewForm} onClose={handleModalClose}>
        <ReviewForm
          productId={productId}
          productName={productName}
          onModalClose={handleFormSuccess}
        />
      </Modal>
    </>
  );
};
